"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Flame, PhoneCall, Pipette as Pipe, ShieldAlert, Waves, Clock, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface EmergencyServiceItem {
  id: string;
  title: string;
  description: string;
  badge: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  estimatedArrival: string;
}
const emergencyServices: EmergencyServiceItem[] = [{
  id: "burst-pipe",
  title: "Burst Pipe Repair",
  description: "Rapid water main shutoff, pinpoint leak isolation, and high-pressure copper or PEX pipe reconstruction before water compromises your home foundation.",
  badge: "Immediate Dispatch",
  icon: Pipe,
  estimatedArrival: "30-45 Min Arrival"
}, {
  id: "water-heater",
  title: "No Hot Water Crisis",
  description: "Emergency tank & tankless diagnosis, heating element replacement, pressure valve stabilization, and fast emergency tank changeouts.",
  badge: "Same-Day Fix",
  icon: Flame,
  estimatedArrival: "30-60 Min Arrival"
}, {
  id: "sewage-backup",
  title: "Sewage Line Backup",
  description: "Urgent hydro-jetting, high-definition camera sewer line inspection, and hazardous waste extraction to prevent severe bio-hazard property damage.",
  badge: "Critical Bio-Hazard",
  icon: ShieldAlert,
  estimatedArrival: "Immediate Response"
}, {
  id: "flooding",
  title: "Indoor Flooding & Overflow",
  description: "Instant structural water extraction, broken sump pump replacement, toilet overflow mitigation, and active interior flood containment.",
  badge: "Priority Queue",
  icon: Waves,
  estimatedArrival: "24/7 Live Unit"
}];
export default function Services() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="emergency-plumbing-services" data-nav="light" className="bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto max-w-[1240px] px-5 sm:px-6 md:px-8">
        
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs md:text-sm font-bold tracking-wide uppercase mb-4">
            <span className="inline-block size-2 rounded-full bg-accent animate-pulse" />
            24/7 Rapid Emergency Response
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-extrabold tracking-tight text-foreground leading-[1.12] mb-4">
            Immediate Help for Severe Plumbing Emergencies
          </h2>
          
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground font-sans leading-relaxed">
            Plumbing disasters do not wait for business hours. When pipes burst or sewage backs up, our fully-stocked mobile trucks in Jonesboro deploy immediately to stop damage and restore safety.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs sm:text-sm font-semibold text-foreground/80">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-primary" />
              <span>Direct Phone Dispatch</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-primary" />
              <span>Upfront Honest Pricing</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-primary" />
              <span>Licensed Local Technicians</span>
            </div>
          </div>
        </div>

        {/* 4-Column Grid of Urgent Service Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {emergencyServices.map((service, index) => {
          const IconComponent = service.icon;
          return <motion.div key={service.id} data-index={index} variants={itemVariants} className="flex">
                <Card className="flex flex-col justify-between w-full rounded-2xl bg-card border-border border transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md relative overflow-hidden group">
                  
                  {/* Decorative Amber Accent Stripe on Card Top */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-accent/40 group-hover:bg-accent transition-[background-color] duration-200" />
                  
                  <div>
                    <CardHeader className="pt-6 pb-4 px-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="size-12 rounded-xl bg-secondary text-primary flex items-center justify-center">
                          <IconComponent className="size-6" />
                        </div>
                        <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-muted text-muted-foreground">
                          {service.badge}
                        </span>
                      </div>
                      
                      <CardTitle className="text-xl font-default font-bold text-card-foreground leading-snug">
                        {service.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="px-6 pb-4">
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {service.description}
                      </CardDescription>

                      <div className="mt-4 pt-3 border-t border-border/60 flex items-center gap-1.5 text-xs font-semibold text-foreground">
                        <Clock className="size-3.5 text-primary shrink-0" />
                        <span>{service.estimatedArrival}</span>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="px-6 pt-2 pb-6">
                    <Button asChild className="w-full rounded-full bg-accent text-accent-foreground font-bold hover:bg-accent/90 shadow-sm transition-[transform,background-color] duration-150 active:scale-95">
                      <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                        <PhoneCall className="size-4" />
                        <span>Call (470) 847-9076</span>
                      </a>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Emergency Triage Quick Action Banner */}
        <div className="mt-12 rounded-2xl bg-secondary border border-border p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <h3 className="text-lg md:text-xl font-default font-bold text-foreground">
              Don&apos;t see your specific plumbing crisis listed?
            </h3>
            <p className="text-sm md:text-base text-muted-foreground font-sans">
              We handle all catastrophic residential and commercial plumbing failures 24 hours a day in Jonesboro, GA.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild className="w-full sm:w-auto rounded-full bg-primary text-primary-foreground font-bold px-6 py-6 hover:bg-primary/90 shadow-sm">
              <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                <PhoneCall className="size-4" />
                <span>Speak to Master Plumber</span>
                <ArrowRight className="size-4 ml-1" />
              </a>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}